import React, { useEffect, useState, useCallback } from "react";
import { WarningCircle, ArrowClockwise, X, CheckCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function BatchDataStatusBanner({ showWhenHealthy = false }) {
  const [state, setState] = useState({ status: null, error: null });
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/batchdata/balance");
      setState({ status: r.data?.status || "error", error: r.data?.error || null });
    } catch (e) {
      setState({ status: "error", error: e?.response?.data?.detail || e?.message || "probe failed" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000); // re-probe every 5 min
    return () => clearInterval(t);
  }, [load]);

  if (dismissed) return null;
  if (loading && state.status === null) return null;

  const isHealthy = state.status === "funded";
  const isDepleted = state.status === "depleted";
  const isMissing = state.status === "not_configured";
  const isError = state.status === "error";

  if (isHealthy && !showWhenHealthy) return null;

  const styles = isHealthy
    ? { border: "border-emerald-500/40", bg: "bg-emerald-500/8", accent: "text-emerald-300", icon: "text-emerald-400" }
    : isDepleted
    ? { border: "border-amber-500/50", bg: "bg-amber-500/8", accent: "text-amber-300", icon: "text-amber-400" }
    : isMissing
    ? { border: "border-zinc-700", bg: "bg-zinc-900/40", accent: "text-zinc-300", icon: "text-zinc-400" }
    : { border: "border-red-500/40", bg: "bg-red-500/8", accent: "text-red-300", icon: "text-red-400" };

  const title = isHealthy
    ? "BatchData wallet is funded"
    : isDepleted
    ? "BatchData wallet is empty — scans will silently return zero leads"
    : isMissing
    ? "BatchData API key not configured"
    : "BatchData probe failed";

  const body = isHealthy
    ? "Automated scans and skip-trace are running normally."
    : isDepleted
    ? "Top up your BatchData balance to resume property scans and skip-trace enrichment. Leads without an owner email are silently dropped, so an empty wallet will look like zero results."
    : isMissing
    ? "Set BATCHDATA_API_KEY in the backend environment to enable off-market property sourcing."
    : `Could not verify wallet status. ${state.error ? `Reason: ${state.error}` : ""}`;

  return (
    <div
      className={`mx-6 mt-4 border ${styles.border} ${styles.bg} px-4 py-3 rounded-sm flex items-start gap-3`}
      data-testid="batchdata-status-banner"
      data-status={state.status}
    >
      {isHealthy ? (
        <CheckCircle size={18} weight="fill" className={`${styles.icon} shrink-0 mt-0.5`} />
      ) : (
        <WarningCircle size={18} weight="fill" className={`${styles.icon} shrink-0 mt-0.5`} />
      )}
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${styles.accent}`} data-testid="batchdata-status-title">{title}</div>
        <div className="text-xs text-zinc-400 mt-0.5">{body}</div>
        {isDepleted && (
          <a
            href="https://app.batchdata.com/billing"
            target="_blank"
            rel="noreferrer"
            className="inline-block text-[11px] mono uppercase tracking-[0.15em] text-amber-300 hover:text-amber-200 underline underline-offset-4 mt-2"
            data-testid="batchdata-topup-link"
          >
            Top up BatchData wallet →
          </a>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={load}
          disabled={loading}
          className="p-1.5 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 rounded-sm transition-colors disabled:opacity-50"
          data-testid="batchdata-refresh-btn"
          aria-label="Re-check BatchData balance"
          title="Re-check balance"
        >
          <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} />
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1.5 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 rounded-sm transition-colors"
          data-testid="batchdata-dismiss-btn"
          aria-label="Dismiss banner"
          title="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
